/**
 * A portfolio's sleeves, and whether each one resolved (LAR-1).
 *
 * **Five statuses, five different fixes**, which is why the shared resolver's values are shown
 * unmapped rather than folded into "unresolved":
 *
 * * `missing` — that strategy has not been written yet. Write it, then revalidate.
 * * `ambiguous` — two of your strategies share the name this sleeve looks up by. Rename one, or
 *   pin the sleeve with a `root_id`.
 * * `nested_portfolio` — the reference points at another portfolio. Not supported; point it at a
 *   DSL strategy.
 * * `invalid_code` — the sleeve exists but its stored code is not JSON-format DSL.
 * * `resolved` — found, and named so the author can confirm it is the one they meant.
 *
 * An author told only "unresolved" would have to guess which of those they are looking at, and four
 * of the five guesses are wrong.
 */
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { PortfolioDependency } from '../labStrategyApi';

/** The right-hand note, in the words that name the fix rather than the state. */
const NOTES: Record<PortfolioDependency['status'], string | null> = {
  resolved: null,
  missing: 'not written yet',
  ambiguous: 'several strategies share this name',
  nested_portfolio: 'a portfolio cannot be a sleeve',
  invalid_code: 'stored code is not JSON-format DSL',
};

function Marker({ status }: { status: PortfolioDependency['status'] }) {
  if (status === 'resolved') {
    return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />;
  }
  // `nested_portfolio` and `invalid_code` are red rather than amber: they cannot be fixed by
  // uploading something, so they are not a step that is merely still outstanding.
  if (status === 'nested_portfolio' || status === 'invalid_code') {
    return <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600" aria-hidden="true" />;
  }
  return <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />;
}

interface Props {
  dependencies: PortfolioDependency[];
  /** Rendered beside the heading — the saved strategy's `last_validated_at`, where there is one. */
  validatedAt?: string | null;
}

export function DependencyList({ dependencies, validatedAt }: Props) {
  if (dependencies.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-1.5">
        <span className="text-xs font-semibold text-slate-600">
          Sleeves ({dependencies.filter((dep) => dep.status === 'resolved').length}/
          {dependencies.length} resolved)
        </span>
        {validatedAt && (
          <span className="text-[11px] text-slate-400">Checked {validatedAt.slice(0, 10)}</span>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {dependencies.map((dep) => (
          <li key={dep.key} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs">
            <Marker status={dep.status} />
            <span
              className={`font-mono ${
                dep.status === 'resolved' ? 'text-slate-700' : 'text-slate-900'
              }`}
            >
              {dep.key}
            </span>
            {dep.resolved_name && <span className="text-slate-500">→ {dep.resolved_name}</span>}
            {NOTES[dep.status] && (
              <span className="font-medium text-amber-700">
                {NOTES[dep.status]}
                {dep.status === 'ambiguous' && dep.ambiguous_matches
                  ? ` (${dep.ambiguous_matches} matches)`
                  : ''}
              </span>
            )}
            {/* The branch weights, where the document has them. An author comparing a portfolio
                against its backtest needs to see which sleeve carries what. */}
            {dep.weight_when_true != null && (
              <span className="ml-auto font-mono text-slate-400">
                {dep.weight_when_true}
                {dep.weight_else != null && ` / ${dep.weight_else}`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
